import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, Copy, FileText, RefreshCw, Trash2, Upload } from 'lucide-react'
import { erfuelltRecht, geldText } from '@symphenia/shared-types'
import { FilterBar } from '@/components/list/FilterBar'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { ExportButton } from '@/components/ui/ExportButton'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { bestaetige } from '@/lib/dialoge'
import { inlineInput } from '@/lib/form-styles'
import { useTerminology } from '@/lib/useTerminology'
import { fetchUnits } from '../api/project-bom.api'
import { fetchJournalTypes } from '../api/projects-head.api'
import { oeffneProjektdatei } from '../api/project-tech.api'
import {
  applyAllLvChanges,
  copyProjectLvItems,
  deleteProjectLv,
  fetchProjectLvItems,
  fetchProjectLvStatistics,
  fetchProjectLvs,
  lvItemToJournal,
  projectLvStatisticsExport,
  setProjectLvUnit,
  type ProjectLvGroup,
  type ProjectLvHead,
  type ProjectLvItem,
  type ProjectLvStatistics,
} from '../api/project-lv.api'
import type { JournalType, ProjectDetail } from '../types/project.types'
import { ProjectLvImportDialog } from './ProjectLvImportDialog'
import { LV_ART, LV_PHASE, LV_ZUORDNUNG, lvMenge } from './lv-labels'

const FILTER_LAYOUT = 'project-lv'
const FILTER_KEYS = ['q', 'state', 'kind']

/** Gruppen in Dateireihenfolge, mehrstufig (14.2-7): Eltern vor Kindern. */
function geordnet(gruppen: ProjectLvGroup[]): ProjectLvGroup[] {
  const kinder = new Map<string | null, ProjectLvGroup[]>()
  for (const g of gruppen) kinder.set(g.parentId, [...(kinder.get(g.parentId) ?? []), g])
  const aus: ProjectLvGroup[] = []
  const lauf = (parent: string | null) => {
    for (const g of kinder.get(parent) ?? []) {
      aus.push(g)
      lauf(g.id)
    }
  }
  lauf(null)
  return aus
}

/**
 * AP-35 Detailblatt 12 4.3 — **Reiter LV.** Das LV ist Quelle, keine Struktur:
 * Hier wird gelesen, markiert und **kopiert**; eingefügt wird, wo man will
 * (Reiter Positionen, ein anderes Projekt, ein Beleg). Kein Anlegen-Knopf, kein
 * „senden an" (Auftraggeber 17.9.2026).
 *
 * Zwei Sichten: **Zuordnung** (was davon schon in Verkaufszeilen steht) und
 * **Statistik** (Mengenkette im Verkauf · geliefert · verrechnet, Nachträge,
 * Verkauf ohne LV-Bezug).
 */
export function ProjectLvTab({ projekt }: { projekt: ProjectDetail }) {
  const { t: uebersetze } = useTranslation()
  const terms = useTerminology()
  const { user } = useAuthStore()
  const darf = (recht: string) => erfuelltRecht(recht, user?.permissions)
  const darfImport = darf('projects.import.run')
  const darfPositionen = darf('projects.position.manage')
  const darfJournal = darf('projects.journal.manage')

  const [lvs, setLvs] = useState<ProjectLvHead[] | null>(null)
  const [lvId, setLvId] = useState<string | null>(null)
  const [daten, setDaten] = useState<{ groups: ProjectLvGroup[]; items: ProjectLvItem[] } | null>(null)
  const [statistik, setStatistik] = useState<ProjectLvStatistics | null>(null)
  const [sicht, setSicht] = useState<'zuordnung' | 'statistik'>('zuordnung')
  const [markiert, setMarkiert] = useState<Set<string>>(new Set())
  const [zu, setZu] = useState<Set<string>>(new Set())
  const [gewaehlt, setGewaehlt] = useState<string | null>(null)
  const [filterWerte, setFilterWerte] = useState<Record<string, ActiveFilter>>({})
  const [nurRest, setNurRest] = useState(false)
  const [nurOffenVerrechnet, setNurOffenVerrechnet] = useState(false)
  const [einheiten, setEinheiten] = useState<{ id: string; code: string }[]>([])
  const [journalArten, setJournalArten] = useState<JournalType[]>([])
  const [journalArt, setJournalArt] = useState('')
  const [importOffen, setImportOffen] = useState(false)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const lv = lvs?.find((l) => l.id === lvId) ?? null
  const aktuelles = lvs?.find((l) => l.isCurrent) ?? null

  const ladeListe = useCallback(
    async (waehle?: string) => {
      try {
        const liste = await fetchProjectLvs(projekt.id)
        setLvs(liste)
        setLvId((vorher) => waehle ?? (vorher && liste.some((l) => l.id === vorher) ? vorher : (liste[0]?.id ?? null)))
      } catch (e) {
        setFehler(apiErrorText(e, 'Das LV ließ sich nicht laden.'))
      }
    },
    [projekt.id],
  )

  const ladeLv = useCallback(async () => {
    if (!lvId) {
      setDaten(null)
      setStatistik(null)
      return
    }
    try {
      const [d, s] = await Promise.all([fetchProjectLvItems(projekt.id, lvId), fetchProjectLvStatistics(projekt.id, lvId)])
      setDaten({ groups: d.groups, items: d.items })
      setStatistik(s)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das LV ließ sich nicht laden.'))
    }
  }, [projekt.id, lvId])

  useEffect(() => {
    void ladeListe()
    fetchUnits().then(setEinheiten).catch(() => undefined)
    fetchJournalTypes()
      .then((arten) => {
        const frei = arten.filter((a) => !a.isSystem && a.isActive)
        setJournalArten(frei)
        setJournalArt(frei[0]?.id ?? '')
      })
      .catch(() => undefined)
  }, [ladeListe])

  useEffect(() => {
    setMarkiert(new Set())
    setGewaehlt(null)
    void ladeLv()
  }, [ladeLv])

  // ── Filter (Suche, Zuordnung, Art) ─────────────────────────────────────────
  const filterFelder = useMemo<FilterFieldDef[]>(
    () => [
      { key: 'q', label: 'Suche (OZ, Text)', type: 'text', category: 'core', operators: ['contains'] },
      {
        key: 'state',
        label: 'Zuordnung',
        type: 'select',
        category: 'core',
        operators: ['is', 'is_not'],
        options: [...(['NONE', 'PARTIAL', 'ASSIGNED', 'OVER'] as const).map((s): { value: string; label: string } => ({ value: s, label: LV_ZUORDNUNG[s].label })), { value: 'CHANGED', label: 'geändert mit Zuordnung' }],
      },
      {
        key: 'kind',
        label: 'Art',
        type: 'select',
        category: 'core',
        operators: ['is', 'is_not'],
        options: Object.entries(LV_ART).map(([value, label]) => ({ value, label: label || 'Normal' })),
      },
    ],
    [],
  )

  const passt = useCallback(
    (i: ProjectLvItem) => {
      const q = filterWerte.q?.value?.trim().toLowerCase()
      if (q && !`${i.oz} ${i.shortText}`.toLowerCase().includes(q)) return false
      const s = filterWerte.state
      if (s?.value) {
        const treffer = s.value === 'CHANGED' ? i.usedIn.some((u) => u.outdated) : i.state === s.value
        if (s.operator === 'is_not' ? treffer : !treffer) return false
      }
      const k = filterWerte.kind
      if (k?.value && (k.operator === 'is_not' ? i.kind === k.value : i.kind !== k.value)) return false
      return true
    },
    [filterWerte],
  )

  const gruppen = useMemo(() => geordnet(daten?.groups ?? []), [daten])
  const sichtbar = useMemo(() => (daten?.items ?? []).filter(passt), [daten, passt])

  /** Eine Gruppe und alle Untergruppen — für „Gruppe markieren" und Einklappen. */
  const unterGruppen = useCallback(
    (id: string): Set<string> => {
      const aus = new Set([id])
      let neu = true
      while (neu) {
        neu = false
        for (const g of daten?.groups ?? []) {
          if (g.parentId && aus.has(g.parentId) && !aus.has(g.id)) {
            aus.add(g.id)
            neu = true
          }
        }
      }
      return aus
    },
    [daten],
  )
  const versteckt = useCallback(
    (groupId: string | null) => {
      let g = gruppen.find((x) => x.id === groupId)
      while (g) {
        if (zu.has(g.id)) return true
        g = gruppen.find((x) => x.id === g!.parentId)
      }
      return false
    },
    [gruppen, zu],
  )

  // ── Handlungen ─────────────────────────────────────────────────────────────
  const kopieren = async () => {
    if (!lvId || !markiert.size) return
    setFehler(null)
    try {
      const r = await copyProjectLvItems(projekt.id, lvId, [...markiert])
      const text = r.skippedText ? ` · ${r.skippedText} Text(e) ohne Menge nicht kopiert — als Journal-Eintrag ablegen?` : ''
      setHinweis(`${markiert.size - r.skippedText} LV-Position(en) in der Zwischenablage — einfügen im Reiter ${terms.positionPlural} (oder in einem Beleg)${text}`)
      setMarkiert(new Set())
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  const loeschen = async () => {
    if (!lv) return
    if (!(await bestaetige(`LV Version ${lv.version} löschen? Die Datei bleibt unter Dokumente.`, { ok: 'Löschen', gefaehrlich: true }))) return
    try {
      await deleteProjectLv(projekt.id, lv.id)
      setHinweis(`LV Version ${lv.version} gelöscht.`)
      await ladeListe()
    } catch (e) {
      const d = (e as { response?: { data?: { code?: string; lines?: number; positions?: number } } }).response?.data
      setFehler(d?.code === 'LV_DELETE_REFERENCED' ? `${d.lines} Verkaufszeilen in ${d.positions} Position(en) verweisen auf dieses LV — erst die Zeilen lösen oder löschen.` : apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  const alleUebernehmen = async () => {
    if (!lv) return
    if (!(await bestaetige(`${lv.changedLines} Verkaufszeile(n) zeigen auf eine ältere Version. Alle offenen Zeilen an Version ${lv.version} angleichen?`, { ok: 'Übernehmen' }))) return
    try {
      const r = await applyAllLvChanges(projekt.id, lv.id)
      setHinweis(`${r.applied} Zeile(n) übernommen${r.skipped ? ` · ${r.skipped} nicht mehr offen oder im LV entfallen` : ''}.`)
      await Promise.all([ladeListe(), ladeLv()])
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  const einheitSetzen = async (item: ProjectLvItem, unitId: string) => {
    if (!lvId) return
    try {
      const r = await setProjectLvUnit(projekt.id, lvId, item.id, { unitId: unitId || null, remember: true })
      setHinweis(`Einheit „${item.unitRaw}" → ${einheiten.find((u) => u.id === unitId)?.code ?? 'ohne'} an ${r.updated} Position(en), für künftige LVs gemerkt.`)
      await Promise.all([ladeListe(), ladeLv()])
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  const insJournal = async (item: ProjectLvItem) => {
    if (!lvId || !journalArt) return
    try {
      await lvItemToJournal(projekt.id, lvId, item.id, journalArt)
      setHinweis(`LV ${item.oz} steht jetzt im Journal.`)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  const umschalten = (id: string) =>
    setMarkiert((vorher) => {
      const neu = new Set(vorher)
      if (neu.has(id)) neu.delete(id)
      else neu.add(id)
      return neu
    })
  const gruppeMarkieren = (g: ProjectLvGroup) => {
    const ids = unterGruppen(g.id)
    const eigene = sichtbar.filter((i) => i.groupId && ids.has(i.groupId)).map((i) => i.id)
    setMarkiert((vorher) => {
      const alle = eigene.every((id) => vorher.has(id))
      const neu = new Set(vorher)
      for (const id of eigene) if (alle) neu.delete(id)
      else neu.add(id)
      return neu
    })
  }

  if (!lvs) return <div className="p-4 text-sm text-muted-foreground">Lädt …</div>

  const detail = daten?.items.find((i) => i.id === gewaehlt) ?? null
  const preise = !!statistik?.prices

  return (
    <div className="space-y-4">
      {/* ── Kopf ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
        {lvs.length > 0 ? (
          <select className={`${inlineInput} !w-auto min-w-64`} value={lvId ?? ''} onChange={(e) => setLvId(e.target.value)}>
            {lvs.map((l) => (
              <option key={l.id} value={l.id}>
                Version {l.version} · {LV_PHASE[l.phase] ?? l.phase} · {l.title}
                {l.isCurrent ? ' (aktuell)' : ''}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-sm text-muted-foreground">Noch kein LV in diesem Projekt.</span>
        )}
        {lv && (
          <span className="text-xs text-muted-foreground">
            {lv.format} {lv.formatVersion}
            {lv.lvDate && ` · ${new Date(lv.lvDate).toLocaleDateString('de-DE')}`} · Preise {lv.hasPrices ? 'ja' : 'nein'}
          </span>
        )}
        <span className="flex-1" />
        {lv?.projectFileId && (
          <button type="button" className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted" onClick={() => void oeffneProjektdatei(lv.projectFileId!, lv.fileName).catch((e) => setFehler(apiErrorText(e, 'Die Datei lässt sich nicht öffnen.')))}>
            <FileText className="h-4 w-4" /> Datei
          </button>
        )}
        {lv?.isCurrent && lv.changedLines > 0 && darfPositionen && (
          <button type="button" className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted" onClick={() => void alleUebernehmen()}>
            <RefreshCw className="h-4 w-4" /> {lv.changedLines} Zeile(n) an Version {lv.version} angleichen
          </button>
        )}
        {lv?.isCurrent && darfImport && darfPositionen && (
          <button type="button" className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm text-destructive hover:bg-muted" onClick={() => void loeschen()}>
            <Trash2 className="h-4 w-4" /> LV löschen
          </button>
        )}
        {darfImport && (
          <button type="button" className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground" onClick={() => setImportOffen(true)}>
            <Upload className="h-4 w-4" /> Import
          </button>
        )}
      </div>

      {lv && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <span>
            <span className="text-muted-foreground">Positionen</span> {lv.counts.items}
          </span>
          <span>
            <span className="text-muted-foreground">zugeordnet</span> {lv.counts.assigned}
          </span>
          <span>
            <span className="text-muted-foreground">teilweise</span> {lv.counts.partial}
          </span>
          {lv.counts.over > 0 && <span className="text-destructive">mehrfach {lv.counts.over}</span>}
          <span>
            <span className="text-muted-foreground">nicht zugeordnet</span> {lv.counts.none}
          </span>
          {[
            ['Eventual', lv.counts.optional],
            ['Wahl', lv.counts.alternative],
            ['Regie', lv.counts.regie],
            ['Zuschlag', lv.counts.surcharge],
            ['Text', lv.counts.text],
          ]
            .filter(([, n]) => (n as number) > 0)
            .map(([k, n]) => (
              <span key={k as string} className="text-muted-foreground">
                {k} {n}
              </span>
            ))}
          {lv.counts.unitsUnknown > 0 && <span className="text-destructive">{lv.counts.unitsUnknown} ohne Einheit</span>}
        </div>
      )}

      {hinweis && (
        <div className="flex items-start justify-between gap-3 rounded-md border border-border bg-muted/40 px-4 py-2 text-sm text-foreground">
          <span>{hinweis}</span>
          <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setHinweis(null)}>
            ×
          </button>
        </div>
      )}
      {fehler && <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">{fehler}</div>}

      {lv && (
        <div className="flex gap-1 border-b border-border">
          {(['zuordnung', 'statistik'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSicht(s)}
              className={`-mb-px border-b-2 px-3 py-1.5 text-sm ${sicht === s ? 'border-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              {s === 'zuordnung' ? 'Zuordnung' : 'Statistik'}
            </button>
          ))}
        </div>
      )}

      {/* ── Sicht Zuordnung (4.3) ─────────────────────────────────────────── */}
      {lv && sicht === 'zuordnung' && daten && (
        <>
          <FilterBar availableFields={filterFelder} value={filterWerte} onChange={setFilterWerte} layoutKey={FILTER_LAYOUT} defaultFieldKeys={FILTER_KEYS} festeFelder />
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
            <div className="overflow-x-auto rounded-lg border border-border bg-card">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/50 text-xs text-muted-foreground">
                  <tr>
                    <th className="w-8 px-2 py-1.5" />
                    <th className="px-2 py-1.5 text-left">OZ</th>
                    <th className="px-2 py-1.5 text-left">Kurztext</th>
                    <th className="px-2 py-1.5 text-right">Menge</th>
                    <th className="px-2 py-1.5 text-left">Einheit</th>
                    {preise && <th className="px-2 py-1.5 text-right">EP</th>}
                    {preise && <th className="px-2 py-1.5 text-right">GP</th>}
                    <th className="px-2 py-1.5 text-left">Zuordnung</th>
                  </tr>
                </thead>
                <tbody>
                  {[null, ...gruppen].map((g) => {
                    const eigene = sichtbar.filter((i) => i.groupId === (g?.id ?? null))
                    if (g && versteckt(g.parentId)) return null
                    if (!g && !eigene.length) return null
                    const offen = !g || !zu.has(g.id)
                    return (
                      <GruppenBlock
                        key={g?.id ?? 'ohne'}
                        gruppe={g}
                        offen={offen}
                        preise={preise}
                        onToggle={() =>
                          g &&
                          setZu((vorher) => {
                            const neu = new Set(vorher)
                            if (neu.has(g.id)) neu.delete(g.id)
                            else neu.add(g.id)
                            return neu
                          })
                        }
                        onMarkieren={() => g && gruppeMarkieren(g)}
                      >
                        {offen &&
                          eigene.map((i) => (
                            <tr
                              key={i.id}
                              onClick={() => setGewaehlt(i.id)}
                              className={`cursor-pointer border-t border-border ${gewaehlt === i.id ? 'bg-accent' : 'hover:bg-accent/40'}`}
                            >
                              <td className="px-2 py-1" onClick={(e) => e.stopPropagation()}>
                                <input type="checkbox" checked={markiert.has(i.id)} onChange={() => umschalten(i.id)} aria-label={`LV ${i.oz} markieren`} />
                              </td>
                              <td className="whitespace-nowrap px-2 py-1 tabular-nums text-muted-foreground" style={{ paddingLeft: `${0.5 + (g?.level ?? -1) * 0.75 + 0.75}rem` }}>
                                {i.oz}
                              </td>
                              <td className="px-2 py-1">
                                {i.shortText}
                                {LV_ART[i.kind] && <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">{LV_ART[i.kind]}</span>}
                                {i.gapText && <span className="ml-2 text-xs text-muted-foreground">Bieterlücke</span>}
                              </td>
                              <td className="px-2 py-1 text-right tabular-nums">{lvMenge(i.quantity)}</td>
                              <td className="px-2 py-1" onClick={(e) => e.stopPropagation()}>
                                {i.unitRaw && !i.unitId ? (
                                  darfImport ? (
                                    <select className={`${inlineInput} !w-24 text-destructive`} value="" onChange={(e) => void einheitSetzen(i, e.target.value)} title={`„${i.unitRaw}" laut LV — übersetzen`}>
                                      <option value="">{i.unitRaw} ?</option>
                                      {einheiten.map((u) => (
                                        <option key={u.id} value={u.id}>
                                          {u.code}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <span className="text-destructive">{i.unitRaw}</span>
                                  )
                                ) : (
                                  (i.unitCode ?? i.unitRaw ?? '')
                                )}
                              </td>
                              {preise && <td className="px-2 py-1 text-right tabular-nums">{geldText(i.unitPrice)}</td>}
                              {preise && <td className="px-2 py-1 text-right tabular-nums">{geldText(i.total)}</td>}
                              <td className="px-2 py-1 text-xs">
                                {i.kind === 'TEXT' ? (
                                  <span className="text-muted-foreground">—</span>
                                ) : i.usedIn.length ? (
                                  <span className={LV_ZUORDNUNG[i.state].klasse}>
                                    → {i.usedIn.map((u) => `Pos. ${u.positionNumber} ${u.positionName} (${lvMenge(u.quantity)})`).join(', ')}
                                    {i.state === 'PARTIAL' && ` · Rest ${lvMenge(i.restQuantity)}`}
                                    {i.state === 'OVER' && ' · mehrfach'}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                      </GruppenBlock>
                    )
                  })}
                  {!sichtbar.length && (
                    <tr>
                      <td colSpan={preise ? 8 : 6} className="px-3 py-6 text-center text-muted-foreground">
                        Keine LV-Position passt zum Filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Rechts: Langtext, Bieterlücke, LB-Verweis, „verwendet in". */}
            <div className="space-y-3 rounded-lg border border-border bg-card p-4 text-sm lg:sticky lg:top-4 lg:self-start">
              {detail ? (
                <>
                  <div>
                    <div className="text-xs text-muted-foreground">LV {detail.oz}</div>
                    <div className="font-medium">{detail.shortText}</div>
                  </div>
                  {detail.introText && <p className="whitespace-pre-line text-xs text-muted-foreground">{detail.introText}</p>}
                  {detail.longText && <p className="max-h-72 overflow-auto whitespace-pre-line">{detail.longText}</p>}
                  {detail.gapText && (
                    <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
                      <div className="text-xs font-medium">Bieterlücke</div>
                      <p className="whitespace-pre-line text-xs">{detail.gapText}</p>
                    </div>
                  )}
                  {detail.lbRef && <div className="text-xs text-muted-foreground">LB-Verweis {detail.lbRef}</div>}
                  {detail.kind === 'SURCHARGE' && detail.parentOz && (
                    <div className="text-xs text-muted-foreground">
                      Zuschlag auf {detail.parentOz}
                      {detail.surchargePercent !== null && ` · ${lvMenge(detail.surchargePercent)} %`}
                    </div>
                  )}
                  <div>
                    <div className="mb-1 text-xs font-medium text-muted-foreground">verwendet in</div>
                    {detail.usedIn.length ? (
                      <ul className="space-y-0.5">
                        {detail.usedIn.map((u) => (
                          <li key={u.salesLineId} className="flex justify-between gap-2">
                            <span>
                              Pos. {u.positionNumber} {u.positionName}
                              {u.outdated && <span className="ml-1 text-xs text-amber-700 dark:text-amber-400">(ältere Version)</span>}
                            </span>
                            <span className="tabular-nums">
                              {lvMenge(u.quantity)} {u.unitCode}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-muted-foreground">noch nirgends</span>
                    )}
                  </div>
                  {darfJournal && journalArten.length > 0 && (
                    <div className="flex items-center gap-2 border-t border-border pt-3">
                      <select className={`${inlineInput} !w-auto`} value={journalArt} onChange={(e) => setJournalArt(e.target.value)} aria-label="Art des Journal-Eintrags">
                        {journalArten.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name}
                          </option>
                        ))}
                      </select>
                      <button type="button" className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted" onClick={() => void insJournal(detail)}>
                        Als Journal-Eintrag
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-muted-foreground">Eine LV-Position wählen — hier stehen Langtext, Bieterlücke und wo sie schon verwendet wird.</p>
              )}
            </div>
          </div>

          <div className="sticky bottom-0 flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-2 text-sm">
            <span className="text-muted-foreground">{markiert.size} markiert</span>
            <button type="button" disabled={!markiert.size} className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-primary-foreground disabled:opacity-50" onClick={() => void kopieren()}>
              <Copy className="h-4 w-4" /> Kopieren
            </button>
          </div>
        </>
      )}

      {/* ── Sicht Statistik (4.4) ─────────────────────────────────────────── */}
      {lv && sicht === 'statistik' && statistik && (
        <Statistik
          statistik={statistik}
          nurRest={nurRest}
          nurOffenVerrechnet={nurOffenVerrechnet}
          setNurRest={setNurRest}
          setNurOffenVerrechnet={setNurOffenVerrechnet}
          exportButton={
            <ExportButton
              request={(format, orientation) => projectLvStatisticsExport(projekt.id, lv.id, { format, orientation })}
              mail={{
                source: 'EXPORT_PROJECT_LV',
                title: uebersetze('mailExport.titles.EXPORT_PROJECT_LV'),
                subject: `${uebersetze('mailExport.titles.EXPORT_PROJECT_LV')} ${projekt.projectNumber} · Version ${lv.version}`,
              }}
            />
          }
        />
      )}

      {importOffen && (
        <ProjectLvImportDialog
          projectId={projekt.id}
          aktuelles={aktuelles}
          onClose={() => setImportOffen(false)}
          onDone={async (neu) => {
            setImportOffen(false)
            setHinweis(`LV Version ${neu.version} importiert: ${neu.counts.items} Positionen. Keine Position wurde angelegt — markieren und kopieren.`)
            await ladeListe(neu.id)
          }}
        />
      )}
    </div>
  )
}

function GruppenBlock({
  gruppe,
  offen,
  preise,
  onToggle,
  onMarkieren,
  children,
}: {
  gruppe: ProjectLvGroup | null
  offen: boolean
  preise: boolean
  onToggle: () => void
  onMarkieren: () => void
  children: React.ReactNode
}) {
  return (
    <>
      {gruppe && (
        <tr className="border-t border-border bg-muted/30">
          <td className="px-2 py-1">
            <input type="checkbox" onChange={onMarkieren} checked={false} aria-label={`Gruppe ${gruppe.oz} markieren`} title="Alle Positionen der Gruppe markieren" />
          </td>
          <td colSpan={preise ? 7 : 5} className="px-2 py-1 font-medium" style={{ paddingLeft: `${0.5 + gruppe.level * 0.75}rem` }}>
            <button type="button" className="inline-flex items-center gap-1" onClick={onToggle}>
              {offen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <span className="tabular-nums text-muted-foreground">{gruppe.oz}</span> {gruppe.name}
            </button>
          </td>
        </tr>
      )}
      {children}
    </>
  )
}

const prozent = (n: number | null | undefined) => (n === null || n === undefined ? '' : `${n.toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`)

function Statistik({
  statistik: s,
  nurRest,
  nurOffenVerrechnet,
  setNurRest,
  setNurOffenVerrechnet,
  exportButton,
}: {
  statistik: ProjectLvStatistics
  nurRest: boolean
  nurOffenVerrechnet: boolean
  setNurRest: (b: boolean) => void
  setNurOffenVerrechnet: (b: boolean) => void
  exportButton: React.ReactNode
}) {
  const zeilen = s.rows.filter((z) => (!nurRest || z.rest > 0) && (!nurOffenVerrechnet || (z.invoicedPercent ?? 0) < 100))
  const gruppenName = new Map(s.groups.map((g) => [g.id, `${g.oz} ${g.name}`]))
  const spalten = 6 + (s.billing ? 3 : 0) + (s.prices ? (s.billing ? 3 : 2) : 0)
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <span>
          <span className="text-muted-foreground">im Verkauf</span> {prozent(s.totals.inSalesPercent)}
        </span>
        {s.billing && (
          <span>
            <span className="text-muted-foreground">verrechnet</span> {prozent(s.totals.invoicedPercent)}
          </span>
        )}
        <span>
          <span className="text-muted-foreground">mit Rest</span> {s.totals.withRest}
        </span>
        <span className="flex-1" />
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={nurRest} onChange={(e) => setNurRest(e.target.checked)} /> nur mit Rest
        </label>
        {s.billing && (
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={nurOffenVerrechnet} onChange={(e) => setNurOffenVerrechnet(e.target.checked)} /> nur verrechnet &lt; 100 %
          </label>
        )}
        {exportButton}
      </div>
      {!s.billing && <p className="text-xs text-muted-foreground">Ohne den Baustein Abrechnung fehlen die Spalten geliefert und verrechnet.</p>}

      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/50 text-xs text-muted-foreground">
            <tr>
              <th className="px-2 py-1.5 text-left">OZ</th>
              <th className="px-2 py-1.5 text-left">Kurztext</th>
              <th className="px-2 py-1.5 text-left">Einheit</th>
              <th className="px-2 py-1.5 text-right">LV-Menge</th>
              <th className="px-2 py-1.5 text-right">im Verkauf</th>
              {s.billing && <th className="px-2 py-1.5 text-right">geliefert</th>}
              {s.billing && <th className="px-2 py-1.5 text-right">verrechnet</th>}
              {s.billing && <th className="px-2 py-1.5 text-right">%</th>}
              <th className="px-2 py-1.5 text-right">Rest</th>
              {s.prices && <th className="px-2 py-1.5 text-right">LV-Betrag</th>}
              {s.prices && <th className="px-2 py-1.5 text-right">Verkauf</th>}
              {s.prices && s.billing && <th className="px-2 py-1.5 text-right">verrechnet</th>}
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z, i) => (
              <Fragment key={z.itemId}>
                {(i === 0 || zeilen[i - 1].groupId !== z.groupId) && z.groupId && (
                  <tr className="border-t border-border bg-muted/30">
                    <td colSpan={spalten} className="px-2 py-1 text-xs font-medium">
                      {gruppenName.get(z.groupId)}
                    </td>
                  </tr>
                )}
                <tr className="border-t border-border">
                  <td className="whitespace-nowrap px-2 py-1 tabular-nums text-muted-foreground">{z.oz}</td>
                  <td className="px-2 py-1">{z.shortText}</td>
                  <td className="px-2 py-1">{z.unitCode}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{lvMenge(z.lvQuantity)}</td>
                  <td className={`px-2 py-1 text-right tabular-nums ${z.over ? 'text-destructive' : ''}`}>
                    {lvMenge(z.inSales)} <span className="text-xs text-muted-foreground">{prozent(z.inSalesPercent)}</span>
                  </td>
                  {s.billing && <td className="px-2 py-1 text-right tabular-nums">{lvMenge(z.delivered)}</td>}
                  {s.billing && <td className="px-2 py-1 text-right tabular-nums">{lvMenge(z.invoiced)}</td>}
                  {s.billing && <td className="px-2 py-1 text-right tabular-nums">{prozent(z.invoicedPercent)}</td>}
                  <td className="px-2 py-1 text-right tabular-nums">{lvMenge(z.rest)}</td>
                  {s.prices && <td className="px-2 py-1 text-right tabular-nums">{geldText(z.lvAmount)}</td>}
                  {s.prices && <td className="px-2 py-1 text-right tabular-nums">{geldText(z.salesAmount)}</td>}
                  {s.prices && s.billing && <td className="px-2 py-1 text-right tabular-nums">{geldText(z.invoicedAmount)}</td>}
                </tr>
              </Fragment>
            ))}
            <tr className="border-t-2 border-border bg-muted/50 font-medium">
              <td colSpan={3} className="px-2 py-1.5">
                {zeilen.length} LV-Positionen
              </td>
              <td colSpan={2 + (s.billing ? 3 : 0) + 1} />
              {s.prices && <td className="px-2 py-1.5 text-right tabular-nums">{geldText(s.totals.lvAmount)}</td>}
              {s.prices && <td className="px-2 py-1.5 text-right tabular-nums">{geldText(s.totals.salesAmount)}</td>}
              {s.prices && s.billing && <td className="px-2 py-1.5 text-right tabular-nums">{geldText(s.totals.invoicedAmount)}</td>}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-border bg-card p-4 text-sm">
          <div className="mb-2 font-medium">
            Nachträge · {s.supplements.count}, davon freigegeben {s.supplements.approved}
            {s.prices && s.supplements.amount !== undefined && (
              <span className="ml-2 font-normal text-muted-foreground">
                {geldText(s.supplements.amount)}
                {s.supplements.shareOfLv !== null && s.supplements.shareOfLv !== undefined && ` · ${prozent(s.supplements.shareOfLv)} vom LV`}
              </span>
            )}
          </div>
          {s.supplements.rows.length ? (
            <ul className="space-y-0.5">
              {s.supplements.rows.map((n) => (
                <li key={n.positionId} className="flex justify-between gap-2">
                  <span>
                    {n.supplementNo !== null ? `N${n.supplementNo} · ` : ''}
                    Pos. {n.number} {n.name}
                    <span className="ml-2 text-xs text-muted-foreground">
                      {n.approvedAt ? `freigegeben ${new Date(n.approvedAt).toLocaleDateString('de-DE')}` : 'nicht freigegeben'} · {n.state === 'INVOICED' ? 'verrechnet' : n.state === 'DELIVERED' ? 'geliefert' : 'offen'}
                    </span>
                  </span>
                  {s.prices && <span className="tabular-nums">{geldText(n.amount)}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-muted-foreground">keine</span>
          )}
        </div>
        <div className="rounded-lg border border-border bg-card p-4 text-sm">
          <div className="mb-1 font-medium">Frei, ohne LV-Bezug</div>
          <p className="text-muted-foreground">
            {s.free.lines} Verkaufszeile(n) außerhalb des LV und der Nachträge
            {s.prices && s.free.amount !== undefined && ` · ${geldText(s.free.amount)}`}
          </p>
        </div>
      </div>
    </div>
  )
}
